import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface UserSummary {
  id: number;
  name: string;
  email: string;
  username: string;
  role: string;
  active: boolean;
  version: number;
  createdAt: string;
  type?: string; // 'promoteur', 'material', 'user'
  phone_number?: string;
  region?: string;
  adresse?: string;
  categorie?: string;
  agency?: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
  // Additional fields from admin view
  passwordHash?: string;
  canResetPassword?: boolean;
  resetEndpoint?: string;
}

export interface ResetPasswordResponse {
  message: string;
  userId: number;
  userEmail: string;
  userName: string;
  newPassword: string;
  resetBy: string;
  resetAt: string;
}

// Paginated response structure for admin user list
export interface PaginatedUsersResponse {
  content: UserSummary[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  searchTerm: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private baseUrl = 'https://app.titv-store-api.com/users';

  constructor(private http: HttpClient) {}

  private getHeaders(): HttpHeaders {
    const authUser = sessionStorage.getItem('auth-user');
    let token = '';
    if (authUser) {
      try {
        const userObj = JSON.parse(authUser);
        token = userObj.token || '';
      } catch (e) {
        console.error('Failed to parse auth-user', e);
      }
    }
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else if (error.error) {
      errorMessage = error.error;
    } else if (error.message) {
      errorMessage = error.message;
    }
    return throwError(() => new Error(errorMessage));
  }

  // Basic list (respects permissions)
  getAllUsers(): Observable<UserSummary[]> {
    return this.http.get<UserSummary[]>(`${this.baseUrl}/list`, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // Admin detailed view (full user list with extra fields)
  getAdminView(): Observable<UserSummary[]> {
    return this.http.get<UserSummary[]>(`${this.baseUrl}/list/admin-view`, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // Users by agency
  getUsersByAgency(agencyId: number): Observable<UserSummary[]> {
    return this.http.get<UserSummary[]>(`${this.baseUrl}/by-agency/${agencyId}`, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // Reset password (admin only)
  resetPassword(userId: number): Observable<ResetPasswordResponse> {
    return this.http.post<ResetPasswordResponse>(`${this.baseUrl}/admin/reset-password/${userId}`, {}, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // Deactivate user (soft delete)
  deactivateUser(userId: number): Observable<string> {
    return this.http.delete(`${this.baseUrl}/delete/${userId}`, { headers: this.getHeaders(), responseType: 'text' })
      .pipe(catchError(this.handleError));
  }

  // Reactivate user
  reactivateUser(userId: number): Observable<string> {
    return this.http.put(`${this.baseUrl}/reactivate/${userId}`, {}, { headers: this.getHeaders(), responseType: 'text' })
      .pipe(catchError(this.handleError));
  }

  // Get single user (optional)
  getUserById(userId: number): Observable<UserSummary> {
    return this.http.get<UserSummary>(`${this.baseUrl}/${userId}`, { headers: this.getHeaders() })
      .pipe(catchError(this.handleError));
  }

  // ==================== NEW PAGINATION ADMIN (EXCLUDING ADMINS) ====================
  /**
   * Get paginated list of all non-admin users (supports search by name/email).
   * @param page zero-based page index (default 0)
   * @param size page size (default 10)
   * @param search optional search term (searches in name, email, etc.)
   */
  getPaginatedUsers(page: number = 0, size: number = 10, search?: string): Observable<PaginatedUsersResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    if (search && search.trim() !== '') {
      params = params.set('search', search.trim());
    }
    return this.http.get<PaginatedUsersResponse>(`${this.baseUrl}/admin/list/paginated`, {
      headers: this.getHeaders(),
      params
    }).pipe(catchError(this.handleError));
  }

  // ==================== SEARCH USERS BY EMAIL (EXCLUDING ADMINS) ====================
  /**
   * Search non-admin users by email (contains match).
   * @param email email string to search for
   */
  searchUsersByEmail(email: string): Observable<UserSummary[]> {
    const params = new HttpParams().set('email', email);
    return this.http.get<UserSummary[]>(`${this.baseUrl}/admin/search`, {
      headers: this.getHeaders(),
      params
    }).pipe(catchError(this.handleError));
  }
}